// DashboardBussiness.tsx
import ApexCharts from "apexcharts";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import "bootstrap-daterangepicker/daterangepicker.css";
import { all_routes } from "../../../router/backendComponents/admin/all_routes";
import ReactApexChart from "react-apexcharts";
import { fetchProfile } from "../../../core/data/redux/profileDetails";
import type { RootState, AppDispatch } from "../../../core/data/redux/store";
import apiRequestCall from "../../../apiHelper/globalHelper";
import type { ApexOptions } from "apexcharts";

const DashboardBussiness: React.FC = () => {
  // routes & hooks
  const chartRef = useRef<HTMLDivElement | null>(null);
  const dispatch = useDispatch<AppDispatch>();
  const profile = useSelector((state: RootState) => state.profile?.data);

  const [statusLoading, setStatusLoading] = useState(false);
  const backendRoute = all_routes.backend;

  interface Permission {
    id: number;
    role_id: number;
    name: string;
    role: {
      permissions: Record<string, Record<string, string>>;
    };
  }
  // KPI states (unchanged)
  const [orderKpi, setOrderKpi] = useState({
    todays_order: 0,
    yesterday_order: 0,
    change_pct: null as number | null,
    label: null as string | null,
  });

  const [quoationKpi, setQuotationKpi] = useState({
    todays_quotation: 0,
    yesterday_quotation: 0,
    change_pct: null as number | null,
    label: null as string | null,
  });

  const [principalKpi, setPrincipalKpi] = useState({
    todays_principals: 0,
    yesterday_principals: 0,
    change_pct: null as number | null,
    label: null as string | null,
  });

  const [partialKpi, setPartialKpi] = useState({
    todays_partial_orders: 0,
    yesterday_partial_orders: 0,
    change_pct: null as number | null,
    label: null as string | null,
  });

  // CompanyChart (dynamic) - unchanged
  const [CompanyChart, setCompanyChart] = useState<any>({
    chart: { height: 240, type: "bar", toolbar: { show: false } },
    colors: ["#212529"],
    responsive: [
      {
        breakpoint: 480,
        options: { legend: { position: "bottom", offsetX: -10, offsetY: 0 } },
      },
    ],
    plotOptions: {
      bar: {
        borderRadius: 10,
        borderRadiusWhenStacked: "all",
        horizontal: false,
        endingShape: "rounded",
        colors: {
          backgroundBarColors: ["#f3f4f5"],
          backgroundBarOpacity: 0.5,
          hover: { enabled: true, borderColor: "#F26522" },
        },
      },
    },
    series: [{ name: "Company", data: [40, 60, 20, 80, 60, 60, 60] }],
    xaxis: {
      categories: ["M", "T", "W", "T", "F", "S", "S"],
      labels: { style: { colors: "#6B7280", fontSize: "13px" } },
    },
    yaxis: { labels: { offsetX: -15, show: false } },
    grid: { borderColor: "#E5E7EB", strokeDashArray: 5, padding: { left: -8 } },
    legend: { show: false },
    dataLabels: { enabled: false },
    fill: { opacity: 1 },
  });

  // Principal chart (month-wise) — default placeholder, will be replaced by API
  const [PrincipalChart, setPrincipalChart] = useState<any>({
    chart: { height: 230, type: "bar", toolbar: { show: false } },
    colors: ["#F44336"],
    plotOptions: {
      bar: {
        borderRadius: 5,
        horizontal: false,
        endingShape: "rounded",
        columnWidth: "40%",
        colors: { backgroundBarColors: ["#f3f4f5"], backgroundBarOpacity: 0.6 },
      },
    },
    series: [
      {
        name: "Top Principal Orders",
        data: Array(12).fill(0),
      },
    ],
    xaxis: {
      categories: [
        "J",
        "F",
        "M",
        "A",
        "M",
        "J",
        "JL",
        "AG",
        "S",
        "O",
        "N",
        "D",
      ],
      labels: { style: { colors: "#6B7280", fontSize: "13px" } },
    },
    yaxis: { labels: { formatter: (v: any) => `${v}` } },
    grid: { borderColor: "#E5E7EB", strokeDashArray: 5 },
    legend: { show: false },
    dataLabels: { enabled: false },
    fill: { opacity: 1 },
    tooltip: {
      enabled: true,
      y: {
        formatter: (val: any) => String(val),
      },
    },
  });

  // holds top principal name per month (index 0 = Jan)
  const [monthTopPrincipals, setMonthTopPrincipals] = useState<
    (string | null)[]
  >(Array(12).fill(null));

  // Top products (unchanged from your earlier)
  const [productLabels, setProductLabels] = useState<string[]>([]);
  const [productSeries, setProductSeries] = useState<number[]>([]);
  const [productTotals, setProductTotals] = useState<any[]>([]);
  const [planLoading, setPlanLoading] = useState(false);

  const permission = useSelector(
    (state: RootState) => state.rolesPermissions?.data
  ) as Permission | null;
  const permissionMap = useMemo(() => {
    return permission?.role?.permissions ?? {};
  }, [permission]);
  // small helper
  const fmt = (n: number) =>
    typeof n === "number" ? new Intl.NumberFormat().format(n) : "—";

  // fetchers
  useEffect(() => {
    dispatch(fetchProfile());
  }, [dispatch]);

  useEffect(() => {
    localStorage.setItem("menuOpened", "Dashboard");
  }, []);

  // KPI fetchers (unchanged)
  const getOrderKpi = async () => {
    setStatusLoading(true);
    try {
      const res = await apiRequestCall("post", backendRoute.todayOrdersKpi, {});
      const result = res?.data?.data ?? {};
      if (res?.data?.code === 200) {
        setOrderKpi({
          todays_order: result.todays_order ?? 0,
          yesterday_order: result.yesterday_order ?? 0,
          change_pct: result.change_pct ?? null,
          label: result.label ?? null,
        });
      }
    } finally {
      setStatusLoading(false);
    }
  };

  const getQuotationKpi = async () => {
    setStatusLoading(true);
    try {
      const res = await apiRequestCall(
        "post",
        backendRoute.todayQuotationKpi,
        {}
      );
      const result = res?.data?.data ?? {};
      if (res?.data?.code === 200) {
        setQuotationKpi({
          todays_quotation: result.todays_quotation ?? 0,
          yesterday_quotation: result.yesterday_quotation ?? 0,
          change_pct: result.change_pct ?? null,
          label: result.label ?? null,
        });
      }
    } finally {
      setStatusLoading(false);
    }
  };

  const getPrincipalKpi = async () => {
    setStatusLoading(true);
    try {
      const res = await apiRequestCall(
        "post",
        backendRoute.todayPrincipalOrdersKpi,
        {}
      );
      const result = res?.data?.data ?? {};
      if (res?.data?.code === 200) {
        setPrincipalKpi({
          todays_principals: result.todays_principals ?? 0,
          yesterday_principals: result.yesterday_principals ?? 0,
          change_pct: result.change_pct ?? null,
          label: result.label ?? null,
        });
      }
    } finally {
      setStatusLoading(false);
    }
  };

  const getPartialKpi = async () => {
    setStatusLoading(true);
    try {
      const res = await apiRequestCall(
        "post",
        backendRoute.todayPartialOrdersKpi,
        {}
      );
      const result = res?.data?.data ?? {};
      if (res?.data?.code === 200) {
        setPartialKpi({
          todays_partial_orders: result.todays_partial_orders ?? 0,
          yesterday_partial_orders: result.yesterday_partial_orders ?? 0,
          change_pct: result.change_pct ?? null,
          label: result.label ?? null,
        });
      }
    } finally {
      setStatusLoading(false);
    }
  };

  // Top companies (weekday) - existing
  const getTopCompanies = async () => {
    try {
      const res = await apiRequestCall(
        "post",
        backendRoute.topCompaniesByWeekday,
        { limit: 7 }
      );
      const payload = res?.data?.data ?? null;
      if (res?.data?.code === 200 && payload) {
        const categories = payload.categories ?? [
          "M",
          "T",
          "W",
          "T",
          "F",
          "S",
          "S",
        ];
        const series = (payload.series ?? []).map((s: any) => ({
          name: s.name,
          data: s.data,
        }));
        setCompanyChart((prev: any) => ({
          ...prev,
          series: series.length ? series : prev.series,
          xaxis: { ...(prev.xaxis ?? {}), categories },
          legend: { ...(prev.legend ?? {}), show: series.length > 1 },
        }));
      }
    } catch (err) {
      /* silent */
    }
  };

  // Top Principals month-wise — UPDATED: produces a single series (top principal count per month)
  const getTopPrincipalsCurrentMonth = async () => {
    try {
      const res = await apiRequestCall(
        "post",
        backendRoute.topPrincipalsMonthWise,
        { limit: 5 }
      );
      const payload = res?.data?.data ?? null;
      if (res?.data?.code === 200 && payload) {
        const categories =
          payload.categories ?? PrincipalChart.xaxis.categories;
        let counts: number[] = Array(12).fill(0);
        let namesPerMonth: (string | null)[] = Array(12).fill(null);
        if (Array.isArray(payload.series) && payload.series.length > 0) {
          if (
            payload.series.length === 1 &&
            Array.isArray(payload.series[0].data)
          ) {
            counts = payload.series[0].data.map((v: any) => Number(v || 0));
          } else {
            const months = 12;
            const perMonthMax = Array(months).fill(0);
            const perMonthName = Array(months).fill(null);
            payload.series.forEach((s: any) => {
              const name = s.name ?? null;
              const dataArr = Array.isArray(s.data)
                ? s.data.map((v: any) => Number(v || 0))
                : [];
              for (let i = 0; i < months; i++) {
                const val = dataArr[i] ?? 0;
                if (val > perMonthMax[i]) {
                  perMonthMax[i] = val;
                  perMonthName[i] = name;
                }
              }
            });
            counts = perMonthMax;
            namesPerMonth = perMonthName;
          }
        }
        if (
          Array.isArray(payload.top_principals) &&
          payload.top_principals.length === 12
        ) {
          namesPerMonth = payload.top_principals.map((n: any) =>
            n ? String(n) : null
          );
          if (
            Array.isArray(payload.series) &&
            payload.series[0] &&
            Array.isArray(payload.series[0].data)
          ) {
            counts = payload.series[0].data.map((v: any) => Number(v || 0));
          }
        }

        if (!Array.isArray(counts) || counts.length !== 12) {
          counts = Array(12).fill(0);
        }
        if (!Array.isArray(namesPerMonth) || namesPerMonth.length !== 12) {
          namesPerMonth = Array(12).fill(null);
        }

        setMonthTopPrincipals(namesPerMonth);

        setPrincipalChart((prev: any) => {
          const newChart = {
            ...prev,
            series: [{ name: "Top Principal Orders", data: counts }],
            xaxis: { ...(prev.xaxis ?? {}), categories },
            legend: { ...(prev.legend ?? {}), show: false },
            tooltip: {
              enabled: true,
              custom: function ({ seriesIndex, dataPointIndex, w }: any) {
                const monthName =
                  (categories && categories[dataPointIndex]) || "";
                const principalName = namesPerMonth[dataPointIndex] || "—";
                const value = counts[dataPointIndex] ?? 0;
                return (
                  '<div class="apexcharts-tooltip-custom">' +
                  `<div style="font-size:13px">${principalName}</strong></div>` +
                  `<div style="font-size:13px;margin-top:6px">Orders: <strong>${value}</strong></div>` +
                  "</div>"
                );
              },
            },
          };
          return newChart;
        });
      }
    } catch (err) {
      console.error(err);
    }
  };

  // Top Products current month (dynamic donut) - unchanged
  const getTopProductsCurrentMonth = async () => {
    setPlanLoading(true);
    try {
      const res = await apiRequestCall(
        "post",
        backendRoute.topProductsCurrentMonth,
        { limit: 5 }
      );
      const payload = res?.data?.data ?? null;
      if (res?.data?.code === 200 && payload) {
        const labels = Array.isArray(payload.labels) ? payload.labels : [];
        const series = Array.isArray(payload.series)
          ? payload.series.map((v: any) => Number(v))
          : [];
        const totals = Array.isArray(payload.totals) ? payload.totals : [];
        setProductLabels(labels);
        setProductSeries(series);
        setProductTotals(totals);
      } else {
        setProductLabels([]);
        setProductSeries([]);
        setProductTotals([]);
      }
    } catch (err) {
      setProductLabels([]);
      setProductSeries([]);
      setProductTotals([]);
    } finally {
      setPlanLoading(false);
    }
  };

  useEffect(() => {
    getOrderKpi();
    getQuotationKpi();
    getPrincipalKpi();
    getPartialKpi();
    getTopCompanies();
    getTopPrincipalsCurrentMonth();
    getTopProductsCurrentMonth();

    // sample static chart (unchanged)
    if (chartRef.current) {
      const options = {
        series: [
          {
            name: "sales",
            colors: ["#FFC38F"],
            data: [
              { x: "Inpipeline", y: 400 },
              { x: "Follow Up", y: 130 },
              { x: "Schedule", y: 248 },
              { x: "Conversation", y: 470 },
              { x: "Won", y: 470 },
              { x: "Lost", y: 180 },
            ],
          },
        ],
        chart: { type: "bar", height: 275 },
        plotOptions: {
          bar: { borderRadiusApplication: "around", columnWidth: "40%" },
        },
        colors: ["#00918E"],
        xaxis: {
          type: "category",
          group: { style: { fontSize: "7px", fontWeight: 700 } },
        },
        yaxis: { min: 0, max: 500, tickAmount: 5 },
      };
      const chart = new ApexCharts(chartRef.current as any, options);
      chart.render();
      return () => chart.destroy();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const location = useLocation();
  const [showLoader, setShowLoader] = useState(false);
  useEffect(() => {
    if (location.pathname === "/dashboard/deals-dashboard") {
      setShowLoader(true);
      setTimeout(() => setShowLoader(false), 2000);
    }
  }, [location.pathname]);

  // DONUT options for products (typed)
  const planOptions: ApexOptions = {
    chart: { type: "donut", toolbar: { show: false } },
    labels: productLabels,
    legend: { position: "bottom" },
    dataLabels: { enabled: false },
    responsive: [
      {
        breakpoint: 480,
        options: {
          chart: { height: 180 },
          legend: { position: "bottom" },
        },
      },
    ],
    tooltip: {
      y: {
        formatter: (val: any) => {
          const total = productSeries.reduce((a, b) => a + b, 0);
          const pct =
            total > 0 ? ((Number(val) / total) * 100).toFixed(2) + "%" : "0%";
          return `${val} (${pct})`;
        },
      },
    },
  };

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="welcome-wrap mb-4">
            <div className=" d-flex align-items-center justify-content-between flex-wrap">
              <div className="mb-3">
                <h2 className="mb-1" style={{ color: "white" }}>
                  Welcome Back, {profile?.name}
                </h2>
              </div>
              <div className="d-flex align-items-center flex-wrap mb-1">
                <Link
                  to="/"
                  className={`btn btn-md mb-2 ${
                    location?.pathname === "/" || "/admin-dashboard"
                      ? "btn-light"
                      : "btn-dark"
                  }`}
                >
                  Admin Dashboard
                </Link>

                {/* {(permissionMap?.website_dashboard?.view_website_dashboard ||
                  permissionMap?.website_dashboard?.edit_website_dashboard) && (
                  <Link
                    to="/website-dashboard"
                    className={`btn btn-md me-2 mb-2 ${
                      location?.pathname === "/website-dashboard"
                        ? "btn-light"
                        : "btn-dark"
                    }`}
                  >
                    Website Dashboard
                  </Link>
                )} */}
              </div>
            </div>
          </div>

          {/* KPI row (unchanged rendering) */}
          <div className="row">
            {/* Today's Order */}
            <div className="col-xl-3 col-sm-6 d-flex">
              <div className="card flex-fill">
                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between">
                    <span className="avatar avatar-md rounded bg-dark mb-3">
                      <i className="ti ti-building fs-16" />
                    </span>

                    {orderKpi?.change_pct == -100 ? (
                      <span className="badge bg-success fw-normal mb-3">
                        {"+0.00% Increase By Yesterday"}
                      </span>
                    ) : (
                      <>
                        {orderKpi?.todays_order > orderKpi?.yesterday_order && (
                          <span className="badge bg-success fw-normal mb-3">
                            {orderKpi?.label}
                          </span>
                        )}
                        {orderKpi?.todays_order < orderKpi?.yesterday_order && (
                          <span className="badge bg-danger fw-normal mb-3">
                            {orderKpi?.label}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <h2 className="mb-1">{orderKpi?.todays_order}</h2>
                      <p className="fs-13">Today's Order</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Today's Quotation */}
            <div className="col-xl-3 col-sm-6 d-flex">
              <div className="card flex-fill">
                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between">
                    <span className="avatar avatar-md rounded bg-dark mb-3">
                      <i className="ti ti-building fs-16" />
                    </span>

                    {quoationKpi?.change_pct == -100 ||
                    quoationKpi?.change_pct == 0 ? (
                      <span className="badge bg-success fw-normal mb-3">
                        {"+0.00% Increase By Yesterday"}
                      </span>
                    ) : (
                      <>
                        {quoationKpi?.todays_quotation >
                          quoationKpi?.yesterday_quotation && (
                          <span className="badge bg-success fw-normal mb-3">
                            {quoationKpi?.label}
                          </span>
                        )}
                        {quoationKpi?.todays_quotation <
                          quoationKpi?.yesterday_quotation && (
                          <span className="badge bg-danger fw-normal mb-3">
                            {quoationKpi?.label}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <h2 className="mb-1">{quoationKpi?.todays_quotation}</h2>
                      <p className="fs-13">Today's Quotation</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Today's Unique Principal Order */}
            <div className="col-xl-3 col-sm-6 d-flex">
              <div className="card flex-fill">
                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between">
                    <span className="avatar avatar-md rounded bg-dark mb-3">
                      <i className="ti ti-chalkboard-off fs-16" />
                    </span>

                    {principalKpi?.change_pct == -100 ? (
                      <span className="badge bg-success fw-normal mb-3">
                        {"+0.00% Increase By Yesterday"}
                      </span>
                    ) : (
                      <>
                        {principalKpi?.todays_principals >
                          principalKpi?.yesterday_principals && (
                          <span className="badge bg-success fw-normal mb-3">
                            {principalKpi?.label}
                          </span>
                        )}
                        {principalKpi?.todays_principals <
                          principalKpi?.yesterday_principals && (
                          <span className="badge bg-danger fw-normal mb-3">
                            {principalKpi?.label}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <h2 className="mb-1">
                        {principalKpi?.todays_principals}
                      </h2>
                      <p className="fs-13">Today's Unique Principal Order</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Today's Partial Order */}
            <div className="col-xl-3 col-sm-6 d-flex">
              <div className="card flex-fill">
                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between">
                    <span className="avatar avatar-md rounded bg-dark mb-3">
                      <i className="ti ti-businessplan fs-16" />
                    </span>

                    {partialKpi?.change_pct == -100 ? (
                      <span className="badge bg-success fw-normal mb-3">
                        {"+0.00% Increase By Yesterday"}
                      </span>
                    ) : (
                      <>
                        {partialKpi?.todays_partial_orders >
                          partialKpi?.yesterday_partial_orders && (
                          <span className="badge bg-success fw-normal mb-3">
                            {partialKpi?.label}
                          </span>
                        )}
                        {partialKpi?.todays_partial_orders <
                          partialKpi?.yesterday_partial_orders && (
                          <span className="badge bg-danger fw-normal mb-3">
                            {partialKpi?.label}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <h2 className="mb-1">
                        {partialKpi?.todays_partial_orders}
                      </h2>
                      <p className="fs-13">Today's Partial Order</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Charts row */}
          <div className="row">
            {/* Top Companies (dynamic) */}
            <div className="col-xxl-3 col-lg-6 d-flex">
              <div className="card flex-fill">
                <div className="card-header pb-2 d-flex align-items-center justify-content-between flex-wrap">
                  <h5 className="mb-2">Top Companies</h5>
                </div>
                <div className="card-body">
                  <ReactApexChart
                    id="company-chart"
                    options={CompanyChart}
                    series={CompanyChart.series}
                    type="bar"
                    height={240}
                  />
                  <p className="f-13 d-inline-flex align-items-center">
                    Top Companies Current Month Order
                  </p>
                </div>
              </div>
            </div>

            {/* Top Principals (dynamic month-wise) */}
            <div className="col-lg-6 d-flex">
              <div className="card flex-fill">
                <div className="card-header pb-2 d-flex align-items-center justify-content-between flex-wrap">
                  <h5 className="mb-2">Top Principals</h5>
                </div>
                <div className="card-body pb-0">
                  <ReactApexChart
                    id="principal-chart"
                    options={PrincipalChart}
                    series={PrincipalChart.series}
                    type="bar"
                    height={230}
                  />
                  <p className="f-13 d-inline-flex align-items-center">
                    Top Principal (highest for each month) - month-wise
                  </p>
                </div>
              </div>
            </div>

            {/* Top Products (dynamic donut) */}
            <div className="col-xxl-3 col-xl-12 d-flex">
              <div className="card flex-fill">
                <div className="card-header pb-2 d-flex align-items-center justify-content-between flex-wrap">
                  <h5 className="mb-2">Top 5 Products</h5>
                </div>
                <div className="card-body">
                  {/* render dynamic donut */}
                  <ReactApexChart
                    options={planOptions}
                    series={productSeries}
                    type="donut"
                    height={240}
                  />

                  {/* simple legend + percentages */}
                  <div className="mt-3">
                    {productLabels.length === 0 && !planLoading && (
                      <div className="text-muted">
                        No product data for current month
                      </div>
                    )}
                    {planLoading && (
                      <div className="text-muted">Loading products...</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DashboardBussiness;
